import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ProductRequest, ProductTypesService, ProductsService, CollectionsService, CategoriesService, ProductAttributeDefinitionsService, FilterRequest, ProductAttributeDefinitionResponse, AttributeDataType, ProductAttributeValueResponse, ProductAttributeDefinitionTranslationResponse, ProductAttributeValueTranslationResponse, AttributeRequest, BrandsService } from '@src/app/shared/backend/clients';

import { SessionService } from '@app/shared/services/session.service';
import { firstValueFrom } from 'rxjs';
import { ComboData } from '@src/app/shared/components/comboData/comboData';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { Features } from '@src/app/shared/backend/auth/Feature';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';

@Component({
    selector: 'app-add-product',
    templateUrl: './add-product.component.html',
    styleUrl: './add-product.component.scss',
    host: {
        class: 'app-add-product'
    },
    standalone: false
})
export class AddProductComponent extends BaseViewComponent implements OnInit {

  canViewPrice = false;
  hasLimitedEditionEnabled = false;
  hasCustomisation = false;
  hasItemsWithoutSerialNumberEnabled = false;
  hasOnlyOneActiveBrand = false;

  productAttributesFeatureEnabled:boolean = false;
  productAttributeDefinitions: ProductAttributeDefinitionResponse[] = [];

  combosData: { [key: string]: ComboData } = {
    productTypes: new ComboData(async (filter) => await firstValueFrom(this.productTypesService.apiProductTypesFilterComboPost(filter))),
    collections: new ComboData(async (filter) => await firstValueFrom(this.collectionsService.apiCollectionsFilterComboPost(filter))),
    categories: new ComboData(async (filter) => await firstValueFrom(this.categoriesService.apiCategoriesFilterComboPost(filter))),

    brands: new ComboData(async (filter) => {
      const activeBrandsFilter = {
        propertyName: 'isActive',
        operator: FilterOperator.EQUALS,
        values: ['true']
      };

      const brandsFilter = {
        ...filter,
        additionalFilters: [
          ...(filter.additionalFilters ?? []),
          activeBrandsFilter
        ]
      };

      return await firstValueFrom(
        this.brandsService.apiBrandsFilterComboPost(brandsFilter)
      );
    })
  };

  public productForm: FormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(50)]],
    sku: ['', [Validators.required]],
    productTypeId: [[], [Validators.required]],
    brandId: [[]],
    collections: [[]],
    categories: [[]],
    price: [null],
    description: ['', [Validators.required, Validators.maxLength(100)]],
    warrantyDurationMonths: ['', [Validators.required]],
    imageUrl: [''],
    comment: [''],
    isSerializable: ['false'],
    isCustomisable: ['false'],
    isWarrantyCardMandatory: ['false'],
    isSerialNumberRequired: ['true'],
    isLimitedEdition: ['false'],
    limitedEditionSize: [1],
    generateLimitedEditionSerialNumbers: [false],
    productAttributes: this.fb.array([])
  });

  constructor(
    public session: SessionService,
    private router: Router,
    private productsService: ProductsService,
    private productAttributeDefinitionsService: ProductAttributeDefinitionsService,
    private productTypesService: ProductTypesService,
    private collectionsService: CollectionsService,
    private categoriesService: CategoriesService,
    private permissionService: PermissionService,
    private brandsService: BrandsService,
    private fb: FormBuilder) {
      super();
    }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    await this.initCombosData();

    const settings = await this.session.getCompanySettings();
    this.productAttributesFeatureEnabled = settings.features?.some(f => f.name === 'ProductAttributes' && f.isEnabled) ?? false;

    this.session.currentCompanyData$.subscribe(company => {
      this.hasLimitedEditionEnabled = company?.hasLimitedEdition ?? false;
      this.canViewPrice = company?.hasPrice ?? false;
      this.hasItemsWithoutSerialNumberEnabled = company?.hasItemsWithoutSerialNumber ?? false;
    });

    this.hasCustomisation = await this.permissionService.hasFeaturePermissions(Features.ProductCustomisations);

    this.productForm.get('isLimitedEdition')?.valueChanges.subscribe(val => {
      const limitedEditionSizeControl = this.productForm.get('limitedEditionSize');

      if (val === 'true') {
        limitedEditionSizeControl?.enable();
        limitedEditionSizeControl?.setValidators([Validators.required, Validators.min(1)]);
      } else {
        limitedEditionSizeControl?.disable();
        limitedEditionSizeControl?.clearValidators();
      }

      limitedEditionSizeControl?.updateValueAndValidity();
    });

    if (this.productAttributesFeatureEnabled) {
      await this.loadProductAttributeDefinitions();
      this.buildDynamicProductAttributes();
    } else {
      this.productAttributeDefinitions = [];
      this.productAttributesArray.clear();
    }
  }

  private async initCombosData(): Promise<void> {
    await Promise.all([
      this.combosData['productTypes'].getData(null, 1),
      this.combosData['collections'].getData(null, 1),
      this.combosData['categories'].getData(null, 1),
      this.combosData['brands'].getData(null, 1)
    ]);

    this.hasOnlyOneActiveBrand = this.combosData['brands'].totalRecords === 1;
    this.autoSelectBrandWhenOnlyOneIsAvailable();
  }

  async onLazyLoadComboData(comboType: 'productTypes' | 'collections' | 'categories' | 'brands', event: any) {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));

    await this.combosData[comboType].getData(search, activePage);
  }

  getComboTotalPages(comboType: 'productTypes' | 'collections' | 'categories' | 'brands'): number {
    const totalRecords = this.combosData[comboType]?.totalRecords ?? 0;
    const pageSize = Number(this.combosData[comboType]?.rowsPerPageOption) || 5;

    return Math.max(1, Math.ceil(totalRecords / pageSize));
  }

  private autoSelectBrandWhenOnlyOneIsAvailable(): void {
    if (!this.hasOnlyOneActiveBrand) {
      return;
    }

    const brandId = this.combosData['brands'].list?.[0]?.id;

    if (brandId == null) {
      return;
    }

    this.productForm.get('brandId')?.setValue([brandId]);
  }

  onProductTypeChange(event: any) {
    const selectedOption = event.selected;

    if (!selectedOption) {
      const productTypeControl = this.productForm.get('productTypeId');
      productTypeControl?.reset();
      productTypeControl?.markAsTouched();
    }
  }

  get productAttributesArray(): FormArray {
    return this.productForm.get('productAttributes') as FormArray;
  }

  isLimitedEditionSizeDisabled(): boolean {
    return this.productForm.get('isLimitedEdition')?.value === 'false';
  }

  private async loadProductAttributeDefinitions(): Promise<void> {
    if (!this.productAttributesFeatureEnabled) {
      this.productAttributeDefinitions = [];
      this.productAttributesArray.clear();
      return;
    }
    const filter: FilterRequest = {
      pageSize: 500,
      pageNumber: 1,
      filters: [
        {
          propertyName: 'isActive',
          operator: FilterOperator.EQUALS,
          values: ['true']
        }
      ]
    };

    this.productAttributeDefinitions =(await firstValueFrom(
      this.productAttributeDefinitionsService.apiProductAttributeDefinitionsFilterPost(filter)
    )).results ?? [];
  }

  private buildDynamicProductAttributes(): void {
    this.productAttributesArray.clear();

    // Build a dynamic form group per definition
    this.productAttributeDefinitions.forEach(definition => {
      this.productAttributesArray.push(this.createProductAttributeGroup(definition));
    });
  }

  private createProductAttributeGroup(definition: ProductAttributeDefinitionResponse): FormGroup {
    const isFixedValues = definition.productAttributeDataType === AttributeDataType.FixedValues;
    const isMultiNumeric = !isFixedValues && definition.allowMultipleValues;

    return this.fb.group({
      definitionId: [definition.id],
      code: [definition.definitionCode],
      productAttributeDataType: [definition.productAttributeDataType],
      allowMultipleValues: [definition.allowMultipleValues],
      value: isFixedValues ? this.fb.control([]) : this.createTextboxValueControl(definition.productAttributeDataType),
      values: this.fb.array(isMultiNumeric ? [this.createTextboxValueControl(definition.productAttributeDataType)] : [])
    });
  }

  private createTextboxValueControl(dataType: AttributeDataType) {
    if (dataType === AttributeDataType.FixedValues) {
      return;
    }

    const validators = []; // [Validators.required]

    if (dataType === AttributeDataType.Integer) {
      validators.push(Validators.pattern(/^-?\d+$/));
    }

    if (dataType === AttributeDataType.Decimal) {
      validators.push(Validators.pattern(/^-?\d+([.,]\d+)?$/));
    }

    return this.fb.control(null, validators);
  }

  getNumericValuesArray(attributeIndex: number): FormArray {
    return this.productAttributesArray.at(attributeIndex).get('values') as FormArray;
  }

  addNumericMultiValue(attributeIndex: number): void {

    const maxDynamicFields = 10;

    const attributeGroup = this.productAttributesArray.at(attributeIndex) as FormGroup;
    const valuesArray = this.getNumericValuesArray(attributeIndex);
    const dataType = attributeGroup.get('productAttributeDataType')?.value;

    if (valuesArray.length >= maxDynamicFields) {
      return;
    }

    valuesArray.push(this.createTextboxValueControl(dataType));
  }

  removeNumericMultiValue(attributeIndex: number, valueIndex: number): void {
    const valuesArray = this.getNumericValuesArray(attributeIndex);

    if (valuesArray.length <= 1) {
      return;
    }

    valuesArray.removeAt(valueIndex);
  }

  isFixedValuesAttribute(attribute: AbstractControl | null): boolean {
    return attribute?.get('productAttributeDataType')?.value === AttributeDataType.FixedValues;
  }

  isTextAttribute(attribute: AbstractControl | null): boolean {
    return attribute?.get('productAttributeDataType')?.value === AttributeDataType.Text;
  }

  isIntegerAttribute(attribute: AbstractControl | null): boolean {
    return attribute?.get('productAttributeDataType')?.value === AttributeDataType.Integer;
  }

  isDecimalAttribute(attribute: AbstractControl | null): boolean {
    return attribute?.get('productAttributeDataType')?.value === AttributeDataType.Decimal;
  }

  allowMultipleValuesAttribute(attribute: AbstractControl | null): boolean {
    return !!attribute?.get('allowMultipleValues')?.value;
  }

  getAttributeLabel(definition: ProductAttributeDefinitionResponse): string {
    // Use translation for current language
    const currentLang = this.session?.locale || 'en';
    const translations = definition?.productAttributeDefinitionTranslations || [];

    const currentTranslation = translations.find((item: ProductAttributeDefinitionTranslationResponse) => item.isoCode === currentLang);

    return currentTranslation?.translatedName || definition?.definitionCode || '';
  }

  getProductAttributeDropdownOptions(definition: ProductAttributeDefinitionResponse): Array<{ id: number; text: string }> {
    const currentLang = this.session?.locale || 'en';
    const values = definition?.productAttributeValues?.filter(x=> x.isActive) || [];

    // response values to dropdown options
    return values.map((item: ProductAttributeValueResponse) => {
      const translations = item.productAttributeValueTranslations || [];
      const currentTranslation = translations.find((translation: ProductAttributeValueTranslationResponse) => translation.isoCode === currentLang);

      return {
        id: item.id || 0,
        text: currentTranslation?.translatedValue || item.valueCode
      };
    });
  }

  async submitForm() {
    if (this.productForm.valid) {
      const collectionIds = this.productForm.controls['collections'].value || [];
      const categoryIds = this.productForm.controls['categories'].value || [];

      let dynamicProductAttributes: AttributeRequest[] = [];

      if (this.productAttributesFeatureEnabled) {
        dynamicProductAttributes = this.productAttributesArray.controls
          .map((productAttributeControl): AttributeRequest => {
            const productAttributeForm = productAttributeControl as FormGroup;

            const attributeDefinitionId = productAttributeForm.get('definitionId')?.value;
            const productAttributeDataType = productAttributeForm.get('productAttributeDataType')?.value;
            const productAttributeAllowsMultipleValues = !!productAttributeForm.get('allowMultipleValues')?.value;

            let productAttributeValues: any[];

            if (productAttributeDataType === AttributeDataType.FixedValues) {
              const selectedDropdownValues = productAttributeForm.get('value')?.value || [];

              productAttributeValues = Array.isArray(selectedDropdownValues) ? selectedDropdownValues : [selectedDropdownValues];

            } else if (productAttributeAllowsMultipleValues) {
              productAttributeValues = (productAttributeForm.get('values') as FormArray).value;
            } else {
              const singleTextValue = productAttributeForm.get('value')?.value;

              productAttributeValues = [singleTextValue];
            }

            const filledProductAttributeValues = productAttributeValues.filter(value =>
              value !== null &&
              value !== undefined &&
              value !== ''
            );

            return {
              attributeDefinitionId: attributeDefinitionId,
              values: filledProductAttributeValues
            };
          })
          .filter(productAttribute => {
            if (!productAttribute.values) {
              return false;
            }

            return productAttribute.values.length > 0;
          });
      }

      const payload: ProductRequest = {
        brandId: this.productForm.controls['brandId'].value?.[0] ?? null,
        name: this.productForm.controls['name'].value,
        sku: this.productForm.controls['sku'].value,
        description: this.productForm.controls['description'].value,
        imageUrl: this.productForm.controls['imageUrl'].value,
        isCustomisable: this.productForm.controls['isCustomisable'].value === 'true',
        warrantyDurationMonths: this.productForm.controls['warrantyDurationMonths'].value,
        productTypeId: this.productForm.controls['productTypeId'].value?.[0],
        collectionIds: collectionIds,
        categoryIds: categoryIds,
        price: this.productForm.controls['price'].value,
        isSerialNumberRequired: this.productForm.controls['isSerialNumberRequired'].value === 'true',
        isLimitedEdition: this.productForm.controls['isLimitedEdition'].value === 'true',
        limitedEditionSize: this.productForm.controls['limitedEditionSize'].value,
        generateLimitedEditionSerialNumbers: this.productForm.controls['generateLimitedEditionSerialNumbers'].value,
        comment: this.productForm.controls['comment'].value,
        productAttributes: dynamicProductAttributes
      };

      // console.log('Create product payload', payload);

      await firstValueFrom(this.productsService.apiProductsCreatePost(payload));

      this.productForm.controls['brandId'].setValue([]);
      this.productForm.controls['name'].setValue(null);
      this.productForm.controls['sku'].setValue(null);
      this.productForm.controls['description'].setValue(null);
      this.productForm.controls['imageUrl'].setValue(null);
      this.productForm.controls['isCustomisable'].setValue(null);
      this.productForm.controls['warrantyDurationMonths'].setValue(null);
      this.productForm.controls['productTypeId'].setValue([]);
      this.productForm.controls['price'].setValue(null);
      this.productForm.controls['isLimitedEdition'].setValue(null);
      this.productForm.controls['limitedEditionSize'].setValue(null);
      this.productForm.controls['generateLimitedEditionSerialNumbers'].setValue(null);
      this.productForm.controls['comment'].setValue(null);
      this.productForm.controls['collections'].setValue([]);
      this.productForm.controls['categories'].setValue([]);
      this.productAttributesArray.clear();

      this.router.navigate(['/products']);
    }
  }
}
